/**
 * Auth Slice Module
 * Manages authentication state in Redux store
 *
 * @module store/auth/auth.slice
 * @see {@link src/app/utils/supabase/client.ts} - Client-side Supabase Auth
 * @see {@link src/app/utils/supabase/server.ts} - Server-side Supabase Auth
 * @see {@link src/services/auth.service.ts} - Auth service implementation
 * @see {@link src/types/userAuth.ts} - Centralized auth type definitions
 */

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { User } from "@supabase/supabase-js";

import {
  addToLocalStorage,
  getFromLocalStorage,
  removeFromLocalStorage,
} from "@/services/localStorage.service";
import {
  type AuthState,
  type customErrorObject,
  createErrorObject,
} from "@/types/userAuth";
import { createClientComponent } from "@/app/utils/supabase/client";

/**
 * Interface for user signup information
 */
interface UserSignUpInfo {
  // Add user signup info fields here
  [key: string]: unknown;
}

/**
 * Initial auth state values with fallback to localStorage for persistence
 *
 * This ensures state is maintained between browser refreshes.
 */
const initialState: AuthState = {
  userIsVerify: getFromLocalStorage("userIsVerify") || null,
  // Parse JSON objects for userSignUpInfo or default to null
  userSignUpInfo: (() => {
    const storedInfo = getFromLocalStorage("userSignUpInfo");
    if (!storedInfo) return null;
    try {
      // If it's already an object, return it
      if (typeof storedInfo === "object") return storedInfo;
      // If it's a JSON string, try to parse it
      return JSON.parse(storedInfo);
    } catch (e) {
      // If parsing fails, return null
      return null;
    }
  })(),
  userSignUpEmail: getFromLocalStorage("userSignUpEmail") || "",
  // Removed deprecated 2FA fields
  // isNeed2fA: "",
  // isGoogle2fA: "",
  // isLinkedIn2fA: "",
  lastError: null,
};

/**
 * Auth slice for Redux store
 * Manages auth state with appropriate actions
 */
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Set user verification status
     */
    setUserIsVerify: (state, action: PayloadAction<string | null>) => {
      state.userIsVerify = action.payload;
      addToLocalStorage("userIsVerify", action.payload);
    },
    /**
     * Set user signup info
     */
    setUserSignUpInfo: (
      state,
      action: PayloadAction<Record<string, unknown> | null>
    ) => {
      state.userSignUpInfo = action.payload;

      // Handle localStorage storage safely
      if (action.payload === null) {
        removeFromLocalStorage("userSignUpInfo");
      } else {
        // Store as JSON string to avoid type issues
        try {
          const jsonString = JSON.stringify(action.payload);
          addToLocalStorage("userSignUpInfo", jsonString);
        } catch (e) {
          console.error("Error serializing userSignUpInfo:", e);
        }
      }
    },
    /**
     * Set user signup email
     */
    setUserSignUpEmail: (state, action: PayloadAction<string>) => {
      state.userSignUpEmail = action.payload;
      addToLocalStorage("userSignUpEmail", action.payload);
    },
    /**
     * Set last auth error
     */
    setLastError: (state, action: PayloadAction<customErrorObject | null>) => {
      state.lastError = action.payload;
    },
    /**
     * Reset auth state (logout)
     */
    resetState: (state) => {
      state.userIsVerify = null;
      state.userSignUpInfo = null;
      state.userSignUpEmail = "";
      state.lastError = null;
      removeFromLocalStorage("userIsVerify");
      removeFromLocalStorage("userSignUpInfo");
      removeFromLocalStorage("userSignUpEmail");
    },
    // Removed deprecated 2FA reducers
    // setIsNeed2fA: (state, action: PayloadAction<string>) => {
    //   state.isNeed2fA = action.payload;
    // },
    // setIsGoogle2fA: (state, action: PayloadAction<string>) => {
    //   state.isGoogle2fA = action.payload;
    // },
    // setIsLinkedIn2fA: (state, action: PayloadAction<string>) => {
    //   state.isLinkedIn2fA = action.payload;
    // },
    /**
     * Log out user by signing out of Supabase and clearing local storage
     * Uses Supabase Auth for session management
     *
     * @see {@link src/app/utils/supabase/client.ts} - Supabase client for auth operations
     */
    userLogOut: (state) => {
      // Sign out of Supabase
      const supabase = createClientComponent();
      supabase.auth.signOut().catch((error) => {
        console.error("Error signing out:", error);
      });

      // Clear local storage and reset state
      removeFromLocalStorage("userVerify");
      state.userIsVerify = null;
      state.lastError = null;
    },
  },
});

// Export reducers and actions
export const authActions = authSlice.actions;
export default authSlice;

/**
 * Safely gets the auth state from the Redux store
 * Provides default values when auth state is undefined (during SSR or initial load)
 *
 * @param state - Root state from Redux store
 * @returns Safe auth state with defaults
 */
export function getAuthState(state: any): AuthState {
  // If auth slice is not initialized yet, return default state
  if (!state || !state.auth) {
    return {
      userIsVerify: null,
      userSignUpInfo: null,
      userSignUpEmail: "",
      lastError: null,
    };
  }

  return state.auth;
}

/**
 * Checks if the user is currently authenticated
 * Uses both local storage and Supabase Auth for verification
 *
 * @returns Promise resolving to authentication status
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    // First check local storage for quick response
    const localVerification = getFromLocalStorage("userIsVerify");
    if (localVerification) return true;

    // Then verify with Supabase Auth
    const supabase = createClientComponent();
    const { data } = await supabase.auth.getSession();
    return !!data.session;
  } catch (error) {
    console.error("Error checking authentication:", error);
    return false;
  }
}
